import {useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {DateField} from '@/components/date-field'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {ListEmptyState} from '@/components/section-card'
import {FieldError} from '@/components/field-error'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useAlcanceBloque,
  useBloqueCargos,
  useConfirmarBloque,
  type AlcanceBloque,
  type BloqueCargos,
  type CargoDelBloque,
} from '@/hooks/empresa/perfil_cargo/use-requisitos'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** `incompletos` no se confirma en bloque: lo que falta cambia cargo por cargo. El grupo «solo catálogo»
 * de la spec no está porque hoy es inalcanzable: el nivel de exposición solo entra por la Matriz. */
const GRUPOS = ['completos', 'incompletos'] as const

type Grupo = (typeof GRUPOS)[number]

function Franja({contador}: {contador: BloqueCargos['contador']}) {
  const {t} = useTranslation()
  return (
    <div className="surface space-y-1 rounded-xl p-4" data-testid="bloque-contador">
      <p className="font-heading text-lg">
        {t('requisitoCargo.bloque.contador', {confirmados: contador.confirmados, total: contador.total})}
      </p>
      <p className="text-sm text-muted-foreground">
        {/* Sin solicitudes enviadas, «bloquean 0» se leería como «nada detenido». */}
        {contador.hay_solicitudes
          ? t('requisitoCargo.bloque.bloquean', {
              count: contador.incompletos,
              solicitudes: contador.solicitudes_bloqueadas,
            })
          : t('requisitoCargo.bloque.sinSolicitudes', {count: contador.incompletos})}
      </p>
    </div>
  )
}

function FilaCargo({
  cargo,
  businessId,
  marcado,
  onMarcar,
}: {
  cargo: CargoDelBloque
  businessId: string
  marcado: boolean
  onMarcar: (marcado: boolean) => void
}) {
  const {t} = useTranslation()
  const confirmable = cargo.grupo !== 'incompletos'
  const claveFila = cargo.solicitudes_detenidas > 0 ? 'filaConSolicitudes' : 'fila'
  return (
    <li
      className="flex flex-wrap items-start gap-3 border-t py-3 first:border-t-0"
      data-testid={`bloque-${cargo.cargo}`}>
      {confirmable && (
        <Checkbox
          id={`bloque-${cargo.position_id}`}
          checked={marcado}
          onCheckedChange={(valor) => onMarcar(valor === true)}
          data-testid={`bloque-marcar-${cargo.cargo}`}
        />
      )}
      <div className="min-w-0 flex-1 space-y-1">
        {confirmable ? (
          <Label htmlFor={`bloque-${cargo.position_id}`} className="font-medium">
            {cargo.cargo}
          </Label>
        ) : (
          <p className="font-medium">{cargo.cargo}</p>
        )}
        <p className="text-xs text-muted-foreground">
          {t(`requisitoCargo.bloque.${claveFila}`, {
            count: cargo.personas,
            requisitos: cargo.requisitos_nacen,
            solicitudes: cargo.solicitudes_detenidas,
          })}
        </p>
        {cargo.falta.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid={`bloque-falta-${cargo.cargo}`}>
            {t('requisitoCargo.bloque.falta', {
              criterios: cargo.falta.map((criterio) => t(`perfilCargo.criterios.${criterio}`)).join(', '),
            })}
          </p>
        )}
      </div>
      <Link className="text-sm underline" to={`/businesses/${businessId}/sgsst/perfiles-cargo/${cargo.position_id}`}>
        {t('requisitoCargo.bloque.abrir')}
      </Link>
    </li>
  )
}

function RojosSinExplicar({alcance, onQuitar}: {alcance: AlcanceBloque; onQuitar: (positionId: string) => void}) {
  const {t} = useTranslation()
  const cargos = [...new Map(alcance.rojos_sin_explicar.map((rojo) => [rojo.position_id, rojo.cargo])).entries()]
  return (
    <div className="notice-amber space-y-2 rounded-lg px-3 py-2 text-sm" data-testid="bloque-rojos-sin-explicar">
      <p>{t('requisitoCargo.bloque.sinExplicar', {count: alcance.rojos_sin_explicar.length})}</p>
      <PorQue label={t('common.porQue')} testId="bloque-rojos-porque" contenidoTestId="bloque-rojos-porque-contenido">
        <p>{t('requisitoCargo.bloque.sinExplicarPorque')}</p>
      </PorQue>
      <ul className="list-disc pl-4 text-xs">
        {alcance.rojos_sin_explicar.map((rojo) => (
          <li key={`${rojo.employee_id}-${rojo.requisito}`}>
            {t('requisitoCargo.bloque.sinExplicarFila', {
              nombre: rojo.nombre,
              cargo: rojo.cargo,
              requisito: rojo.requisito,
            })}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {cargos.map(([positionId, cargo]) => (
          <Button
            key={positionId}
            size="sm"
            variant="outline"
            data-testid={`bloque-quitar-${cargo}`}
            onClick={() => onQuitar(positionId)}>
            {t('requisitoCargo.bloque.quitarDeLaSeleccion', {cargo})}
          </Button>
        ))}
      </div>
    </div>
  )
}

function ResumenDialog({
  alcance,
  exigible,
  motivo,
  hoy,
  error,
  pendiente,
  onExigible,
  onMotivo,
  onCerrar,
  onFirmar,
  onRecalcular,
  onQuitar,
}: {
  alcance: AlcanceBloque
  exigible: string
  motivo: string
  hoy: string
  error: string | null
  pendiente: boolean
  onExigible: (valor: string) => void
  onMotivo: (valor: string) => void
  onCerrar: () => void
  onFirmar: () => void
  onRecalcular: () => void
  onQuitar: (positionId: string) => void
}) {
  const {t} = useTranslation()
  // El motivo es opcional a propósito: la primera confirmación de un perfil no corrige nada.
  const [invalido, setInvalido] = useState<string | null>(null)
  const detiene = alcance.rojos_sin_explicar.length > 0
  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('requisitoCargo.bloque.resumenTitulo', {count: alcance.cargos})}</DialogTitle>
          <DialogDescription>
            {t('requisitoCargo.bloque.resumen', {
              count: alcance.personas,
              exigibles: alcance.requisitos_exigibles,
              distintos: alcance.requisitos_distintos,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <ul className="text-sm" data-testid="bloque-resumen-cargos">
            {alcance.cargos_detalle.map((cargo) => (
              <li key={cargo.position_id}>
                {t('requisitoCargo.bloque.cargoDelResumen', {cargo: cargo.cargo, count: cargo.personas})}
              </li>
            ))}
          </ul>
          <p className="text-sm" data-testid="bloque-rojos">
            {t('requisitoCargo.bloque.rojos', {
              vencidos: alcance.rojos_por_soporte_vencido,
              ingreso: alcance.rojos_por_examen_ingreso,
            })}
          </p>
          {detiene && <RojosSinExplicar alcance={alcance} onQuitar={onQuitar} />}
          <form
            noValidate
            className="space-y-3"
            onSubmit={(evento) => {
              evento.preventDefault()
              const fallo = validateRequired(exigible)
              setInvalido(fallo)
              if (!fallo) onFirmar()
            }}>
            <DateField
              id="bloque-exigible"
              label={t('requisitoCargo.bloque.exigible')}
              value={exigible}
              min={hoy}
              hint={t('requisitoCargo.bloque.exigibleAyuda')}
              onChange={(valor) => {
                setInvalido(null)
                onExigible(valor)
              }}
            />
            <FieldError error={invalido ?? undefined} />
            <div className="space-y-1">
              <Label htmlFor="bloque-motivo">{t('requisitoCargo.bloque.motivo')}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="bloque-motivo"
                value={motivo}
                onChange={(evento) => onMotivo(evento.target.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onCerrar} data-testid="bloque-cancelar">
                {t('requisitoCargo.bloque.cancelar')}
              </Button>
              <Button type="button" variant="outline" onClick={onRecalcular} data-testid="bloque-recalcular">
                {t('requisitoCargo.bloque.recalcular')}
              </Button>
              <Button type="submit" disabled={pendiente || detiene} data-testid="bloque-firmar">
                {t('requisitoCargo.bloque.firmar')}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function PerfilesCargoConfirmarPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const bloque = useBloqueCargos(business ? businessId : undefined)
  const pedirAlcance = useAlcanceBloque(businessId!)
  const confirmar = useConfirmarBloque(businessId!)
  const [marcados, setMarcados] = useState<Record<string, boolean>>({})
  const [alcance, setAlcance] = useState<AlcanceBloque | null>(null)
  const [exigible, setExigible] = useState('')
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  if (guard) return guard
  const puedeFirmar = capabilities.sgsst_firmar && !godReadOnly
  // El cinturón de modo dios no es la puerta de firmante: con él puesto, `firmar_bloqueado_por` llega en `null`.
  const razonBloqueo = godReadOnly ? t('perfilCargo.bloqueoModoDios') : capabilities.firmar_bloqueado_por
  const cargos = bloque.data?.cargos ?? []
  const seleccion = cargos.filter((cargo) => marcados[cargo.position_id]).map((cargo) => cargo.position_id)

  const pedir = (ids: string[]) => {
    setError(null)
    pedirAlcance.mutate(ids, {
      onSuccess: (data) => {
        setAlcance(data)
        // Recalcular no pisa la fecha que la persona ya escribió.
        if (!exigible) setExigible(data.hoy)
      },
      onError: (err) => setError(translateApiError(err)),
    })
  }

  const quitarDeLaSeleccion = (positionId: string) => {
    const siguiente = {...marcados, [positionId]: false}
    setMarcados(siguiente)
    pedir(cargos.filter((cargo) => siguiente[cargo.position_id]).map((cargo) => cargo.position_id))
  }

  const firmar = () => {
    if (!alcance) return
    confirmar.mutate(
      {positionIds: seleccion, exigibleDesde: exigible, motivo, alcanceToken: alcance.alcance_token},
      {
        onSuccess: (data) => {
          setAlcance(null)
          setMarcados({})
          setExigible('')
          setAviso(t('requisitoCargo.bloque.listo', {count: data.confirmados, requisitos: data.requisitos_creados}))
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <div className="space-y-5" data-testid="perfiles-cargo-confirmar">
      <Link className="text-sm underline" to={`/businesses/${businessId}/sgsst/perfiles-cargo`}>
        {t('requisitoCargo.bloque.volver')}
      </Link>
      <PageHeading
        title={t('requisitoCargo.bloque.titulo')}
        subtitle={business!.name}
        accion={
          !bloque.isLoading &&
          !bloque.isError &&
          cargos.length > 0 && (
            <Button
              disabled={!puedeFirmar || seleccion.length === 0 || pedirAlcance.isPending}
              data-testid="bloque-ver-resumen"
              onClick={() => pedir(seleccion)}>
              {seleccion.length === 0
                ? t('requisitoCargo.bloque.verResumenVacio')
                : t('requisitoCargo.bloque.verResumen', {count: seleccion.length})}
            </Button>
          )
        }
      />
      {!bloque.isLoading && !bloque.isError && cargos.length > 0 && !puedeFirmar && razonBloqueo && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="bloque-sin-firma">
          {t('requisitoCargo.bloque.sinFirma', {motivo: razonBloqueo})}
        </p>
      )}
      {bloque.isLoading && <Skeleton className="h-40 w-full" />}
      {bloque.isError && (
        <p className="text-sm text-destructive" data-testid="bloque-error">
          {t('requisitoCargo.bloque.errorCarga')}
        </p>
      )}
      {!bloque.isLoading && !bloque.isError && (
        <>
          {bloque.data && <Franja contador={bloque.data.contador} />}
          {aviso && (
            <p className="notice-green max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="bloque-aviso">
              {aviso}
            </p>
          )}
          {cargos.length === 0 ? (
            /* Rótulo propio y no el de la miga: dos enlaces iguales al mismo sitio rompen el modo estricto
               de Playwright. */
            <div data-testid="bloque-vacio">
              <ListEmptyState
                page
                title={t('requisitoCargo.bloque.vacio')}
                action={
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/businesses/${businessId}/sgsst/perfiles-cargo`}>
                      {t('requisitoCargo.bloque.armarPerfil')}
                    </Link>
                  </Button>
                }
              />
            </div>
          ) : (
            GRUPOS.map((grupo) => <GrupoDeCargos key={grupo} {...{grupo, cargos, businessId, marcados, setMarcados}} />)
          )}
          {error && !alcance && <p className="text-sm text-destructive">{error}</p>}
        </>
      )}
      {alcance && (
        <ResumenDialog
          alcance={alcance}
          exigible={exigible}
          motivo={motivo}
          hoy={alcance.hoy}
          error={error}
          pendiente={confirmar.isPending || pedirAlcance.isPending}
          onExigible={setExigible}
          onMotivo={setMotivo}
          onCerrar={() => setAlcance(null)}
          onFirmar={firmar}
          onRecalcular={() => pedir(seleccion)}
          onQuitar={quitarDeLaSeleccion}
        />
      )}
    </div>
  )
}

function GrupoDeCargos({
  grupo,
  cargos,
  businessId,
  marcados,
  setMarcados,
}: {
  grupo: Grupo
  cargos: CargoDelBloque[]
  businessId: string | undefined
  marcados: Record<string, boolean>
  setMarcados: (siguiente: Record<string, boolean>) => void
}) {
  const {t} = useTranslation()
  const delGrupo = cargos.filter((cargo) => cargo.grupo === grupo)
  if (delGrupo.length === 0) return null
  const todos = delGrupo.every((cargo) => marcados[cargo.position_id])
  return (
    <section className="surface space-y-2 rounded-xl p-4" data-testid={`bloque-grupo-${grupo}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-1.5 font-heading text-lg">
          {t(`requisitoCargo.bloque.grupos.${grupo}`)}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t(`requisitoCargo.bloque.grupos.${grupo}`)})}
            testId={`bloque-grupo-criterio-${grupo}`}>
            {t(`requisitoCargo.bloque.ayuda.${grupo}`)}
          </HelpIcon>
        </h2>
        <Tag tone="neutral">{t('requisitoCargo.bloque.cuantos', {count: delGrupo.length})}</Tag>
      </div>
      {grupo !== 'incompletos' && (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`bloque-todos-${grupo}`}
            checked={todos}
            onCheckedChange={(valor) =>
              setMarcados({
                ...marcados,
                ...Object.fromEntries(delGrupo.map((cargo) => [cargo.position_id, valor === true])),
              })
            }
            data-testid={`bloque-marcar-todos-${grupo}`}
          />
          <Label htmlFor={`bloque-todos-${grupo}`} className="text-sm">
            {t('requisitoCargo.bloque.marcarTodos', {count: delGrupo.length})}
          </Label>
        </div>
      )}
      <ul>
        {delGrupo.map((cargo) => (
          <FilaCargo
            key={cargo.position_id}
            cargo={cargo}
            businessId={businessId!}
            marcado={!!marcados[cargo.position_id]}
            onMarcar={(marcado) => setMarcados({...marcados, [cargo.position_id]: marcado})}
          />
        ))}
      </ul>
    </section>
  )
}
